import {
  EManifestWasteUnit,
  RcraEpisodicProjectType,
  RcraHsmLandBasedUnit,
  RcraHsmNotificationReason,
  RcraLqgClosureType,
} from "generated-graphql/graphql";

/**
 * Display labels for the RCRA 8700-12 filing-notification enums (§13/§15/§16 and
 * the reused waste UOM). Shared by the live editor (`FilingNotificationsStep`)
 * and the read-only frozen view (`FrozenFilingNotificationsPanel`) so the two
 * cannot drift.
 */

export const EPISODIC_PROJECT_TYPE_LABELS: Record<RcraEpisodicProjectType, string> = {
  [RcraEpisodicProjectType.UnplannedAccidentalSpills]: "Unplanned — accidental spills",
  [RcraEpisodicProjectType.UnplannedActsOfNature]: "Unplanned — acts of nature",
  [RcraEpisodicProjectType.UnplannedProductionProcessUpsets]:
    "Unplanned — production process upsets",
  [RcraEpisodicProjectType.UnplannedProductRecalls]: "Unplanned — product recalls",
  [RcraEpisodicProjectType.UnplannedOther]: "Unplanned — other",
  [RcraEpisodicProjectType.PlannedTankCleanouts]: "Planned — tank cleanouts",
  [RcraEpisodicProjectType.PlannedEquipmentMaintenanceDuringPlantShutdown]:
    "Planned — equipment maintenance during plant shutdown",
  [RcraEpisodicProjectType.PlannedShortTermConstructionOrDemolition]:
    "Planned — short-term construction or demolition",
  [RcraEpisodicProjectType.PlannedExcessChemicalInventoryRemoval]:
    "Planned — excess chemical inventory removal",
  [RcraEpisodicProjectType.PlannedOther]: "Planned — other",
};

export const CLOSURE_TYPE_LABELS: Record<RcraLqgClosureType, string> = {
  [RcraLqgClosureType.CentralAccumulationArea]: "Central accumulation area",
  [RcraLqgClosureType.EntireFacility]: "Entire facility",
};

export const HSM_REASON_LABELS: Record<RcraHsmNotificationReason, string> = {
  [RcraHsmNotificationReason.BeginManaging]: "Beginning to manage HSM",
  [RcraHsmNotificationReason.Managing]: "Currently managing HSM",
  [RcraHsmNotificationReason.StopManaging]: "Stopped managing HSM",
};

export const HSM_LAND_BASED_UNIT_LABELS: Record<RcraHsmLandBasedUnit, string> = {
  [RcraHsmLandBasedUnit.Na]: "Not applicable",
  [RcraHsmLandBasedUnit.Ot]: "Other",
  [RcraHsmLandBasedUnit.Pl]: "Pile",
  [RcraHsmLandBasedUnit.Si]: "Surface impoundment",
};

export const WASTE_UNIT_LABELS: Record<EManifestWasteUnit, string> = {
  [EManifestWasteUnit.P]: "Pounds",
  [EManifestWasteUnit.K]: "Kilograms",
  [EManifestWasteUnit.T]: "Tons (2000 lb)",
  [EManifestWasteUnit.M]: "Metric tons (1000 kg)",
  [EManifestWasteUnit.G]: "Gallons",
  [EManifestWasteUnit.L]: "Liters",
  [EManifestWasteUnit.Y]: "Cubic yards",
  [EManifestWasteUnit.N]: "Cubic meters",
  [EManifestWasteUnit.B]: "Barrels (42 gal)",
};

/**
 * Units selectable on RCRAInfo-bound episodic waste lines. Barrels is an
 * Encamp extension, not an EPA 8700-12 code, so it is excluded here while
 * remaining in `WASTE_UNIT_LABELS` for display of manifest-sourced data.
 */
export const EPISODIC_WASTE_UNIT_LABELS: Record<
  Exclude<EManifestWasteUnit, EManifestWasteUnit.B>,
  string
> = {
  [EManifestWasteUnit.P]: "Pounds",
  [EManifestWasteUnit.K]: "Kilograms",
  [EManifestWasteUnit.T]: "Tons (2000 lb)",
  [EManifestWasteUnit.M]: "Metric tons (1000 kg)",
  [EManifestWasteUnit.G]: "Gallons",
  [EManifestWasteUnit.L]: "Liters",
  [EManifestWasteUnit.Y]: "Cubic yards",
  [EManifestWasteUnit.N]: "Cubic meters",
};
